import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { Building2, Loader2 } from 'lucide-react';

function StatCard({ title, value, subtitle, icon }) {
  return (
    <div className="bg-[#111113] border border-gray-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-gray-400 text-sm">{title}</span>
        <div className="w-8 h-8 bg-purple-900/50 rounded-lg flex items-center justify-center text-purple-400">
          {icon}
        </div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">{value}</div>
      {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
    </div>
  );
}

const statusColors = {
  completed: 'bg-green-900/50 text-green-400',
  'in-progress': 'bg-blue-900/50 text-blue-400',
  ringing: 'bg-yellow-900/50 text-yellow-400',
  queued: 'bg-gray-700 text-gray-300',
  failed: 'bg-red-900/50 text-red-400',
  'no-answer': 'bg-orange-900/50 text-orange-400',
  busy: 'bg-orange-900/50 text-orange-400',
};

export default function VoiceOverview() {
  const { fetchApi } = useCloudAuth();
  const { currentProfile, loading: profileLoading } = useBusinessProfile();
  const [stats, setStats] = useState(null);
  const [mtdCost, setMtdCost] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reload data when business profile changes
  useEffect(() => {
    if (currentProfile?.id) {
      loadData();
    }
  }, [currentProfile?.id]);

  const loadData = async () => {
    if (!currentProfile?.id) return;
    
    setLoading(true);
    try {
      const profileParam = `businessProfileId=${currentProfile.id}`;
      // Load stats, MTD, and recent calls in parallel
      const [statsRes, mtdRes, callsRes] = await Promise.all([
        fetchApi(`/api/cloud/voice/overview?${profileParam}`),
        fetchApi(`/api/cloud/billing/voice/mtd?${profileParam}`),
        fetchApi(`/api/cloud/voice/calls?page=1&limit=10&${profileParam}`)
      ]);
      setStats(statsRes.data);
      setMtdCost(mtdRes.data);
      setRecentCalls(callsRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  if (profileLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!currentProfile) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Building2 className="w-12 h-12 text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No Business Profile Selected</h3>
        <p className="text-gray-400">Select a business profile from the dropdown above to view overview.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Profile Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-purple-400" />
          Overview for {currentProfile.name}
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Call statistics and recent activity
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard
          title="Calls (7 days)"
          value={stats?.stats7d?.totalCalls || 0}
          subtitle={`${stats?.stats7d?.completedCalls || 0} completed`}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          }
        />
        <StatCard
          title="Calls (30 days)"
          value={stats?.stats30d?.totalCalls || 0}
          subtitle={`${stats?.stats30d?.completedCalls || 0} completed`}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          }
        />
        <StatCard
          title="Minutes (7d)"
          value={stats?.stats7d?.totalMinutes?.toFixed(1) || '0'}
          subtitle={`${stats?.stats30d?.totalMinutes?.toFixed(1) || '0'} (30d)`}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Cost (MTD)"
          value={`$${mtdCost?.totalCost?.toFixed(2) || '0.00'}`}
          subtitle={`${mtdCost?.totalEvents || 0} events`}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Success Rate"
          value={stats?.stats30d?.totalCalls > 0 
            ? `${Math.round((stats.stats30d.completedCalls / stats.stats30d.totalCalls) * 100)}%`
            : 'N/A'
          }
          subtitle={`${stats?.stats30d?.failedCalls || 0} failed (30d)`}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      </div>

      {/* Recent Calls Table */}
      <div className="bg-[#111113] border border-gray-800 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-medium text-white">Recent Calls</h3>
          <Link to="/cloud/voice/calls" className="text-sm text-purple-400 hover:text-purple-300">
            View all →
          </Link>
        </div>
        
        {recentCalls.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-800 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No calls yet</h3>
            <p className="text-gray-400 mb-2">Your voice agent is ready. Calls will appear here once you start receiving them.</p>
            <p className="text-sm text-gray-500">Configure your Twilio integration to start receiving calls</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Call ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {recentCalls.map((call) => (
                <tr 
                  key={call._id} 
                  className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                  onClick={() => window.location.href = `/cloud/voice/calls/${call._id}`}
                  data-testid={`recent-call-${call._id}`}
                >
                  <td className="px-6 py-4">
                    <span className="text-purple-400 font-mono text-sm">
                      {call.callId?.slice(0, 16) || call._id.slice(0, 8)}...
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-300 text-sm">{formatDate(call.startedAt)}</td>
                  <td className="px-6 py-4 text-gray-300 text-sm">{formatDuration(call.durationSec)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[call.status] || 'bg-gray-700 text-gray-300'}`}>
                      {call.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-300 text-sm">${call.cost?.toFixed(2) || '0.00'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
